import SuperFetch from 'library/helpers/superFetch';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { getApiError } from 'library/helpers/getApiError';
import { partnerActions } from './slice';
import { fetchPartnerIdentity } from 'library/helpers/partnerIdentity';

// Backend mount is /v5/:orgID/partner (camelCase) — SuperFetch prepends /v5/{orgID}.
// The whole surface is dark behind `requireFeature('partnerPortal')`; callers gate dispatch on the FE flag.
const BASE = '/partner';

// 🔥 Load the signed-in user's partner context for the selected org (identity + grant summary + role).
export function* getPartnerContext() {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const res = yield call(() => SuperFetch.get(`${BASE}/context`));
		if (res.statusCode === 200) {
			yield put(partnerActions.loadPartnerContextSuccess({ orgID, data: res.data }));
		} else if (res.statusCode === 403) {
			// requireNotReadOnly sits on the partner router; a read-only caller (or flag-off) 403s.
			// Not an error — degrade quietly so the shell can hide itself.
			yield put(partnerActions.loadPartnerContextForbidden({ orgID }));
		} else {
			throw Error(getApiError(res) || 'Failed to load partner context.');
		}
	} catch (error) {
		yield put(partnerActions.loadPartnerContextError(error));
	}
}

// 🔥 Resolve the caller's USER-scoped partner identity from the ORG-LESS partner-portal boundary, so the
// Partner tile can follow the person rather than a per-org flag (see library/helpers/partnerIdentity.js).
// Never throws and never notifies: "not a partner" is the normal answer for nearly everyone, and the
// helper already collapses every failure to null. takeLatest + the identityChecked guard in the launcher
// keep this to one request per session.
export function* getPartnerIdentity() {
	const identity = yield call(fetchPartnerIdentity);
	yield put(partnerActions.loadPartnerIdentityResolved(identity));
}

// 🔥 Load the org's installer-grant roster (customer-admin view). Optional ?personID filter.
export function* getGrants({ payload }) {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const query = payload?.personID ? { personID: payload.personID } : {};
		const res = yield call(() => SuperFetch.get(`${BASE}/grants`, {}, query));
		if (res.statusCode === 200) {
			yield put(partnerActions.loadGrantsSuccess({ orgID, data: res.data }));
		} else if (res.statusCode === 403) {
			// The roster is admin-gated (Phase 2). An aligned caller never sends this — the panel
			// self-gates on the same authority — so a 403 means authority drift; degrade quietly to an
			// empty roster instead of a red error toast (mirrors the context path's graceful 403).
			yield put(partnerActions.loadGrantsSuccess({ orgID, data: [] }));
		} else {
			throw Error(getApiError(res) || 'Failed to load installer grants.');
		}
	} catch (error) {
		yield put(partnerActions.loadGrantsError(error));
	}
}

// 🔥 Issue a scoped installer device grant.
// payload = { personID, scopeType:'site'|'org', siteID?, capability:'view_devices'|'manage_devices', expiresAt? }
// ⚠️ `source` is deliberately NOT sent — the backend sets and validates source='manual' for client-issued grants.
export function* createGrant({ payload }) {
	try {
		const res = yield call(() => SuperFetch.post(`${BASE}/grants`, payload));
		if (res.statusCode === 200 || res.statusCode === 201) {
			Notification('success', 'Installer access granted.');
			yield put(partnerActions.createGrantSuccess());
		} else {
			throw Error(getApiError(res) || 'Failed to grant installer access.');
		}
	} catch (error) {
		yield put(partnerActions.createGrantError(error));
		Notification('error', error.message || 'Failed to grant installer access.');
	}
}

// 🔥 Revoke a grant (idempotent server-side).
export function* revokeGrant({ payload }) {
	try {
		const { grantID } = payload;
		const res = yield call(() => SuperFetch.post(`${BASE}/grants/${grantID}/revoke`, {}));
		if (res.statusCode === 200) {
			Notification('success', 'Installer access revoked.');
			yield put(partnerActions.revokeGrantSuccess());
		} else {
			throw Error(getApiError(res) || 'Failed to revoke installer access.');
		}
	} catch (error) {
		yield put(partnerActions.revokeGrantError(error));
		Notification('error', error.message || 'Failed to revoke installer access.');
	}
}

// 🔥 Renew (extend) a grant's expiry. payload = { grantID, expiresAt? } — omit expiresAt for the +90d default.
export function* renewGrant({ payload }) {
	try {
		const { grantID, expiresAt } = payload;
		const body = expiresAt ? { expiresAt } : {};
		const res = yield call(() => SuperFetch.post(`${BASE}/grants/${grantID}/renew`, body));
		if (res.statusCode === 200) {
			Notification('success', 'Installer access renewed.');
			yield put(partnerActions.renewGrantSuccess());
		} else {
			throw Error(getApiError(res) || 'Failed to renew installer access.');
		}
	} catch (error) {
		yield put(partnerActions.renewGrantError(error));
		Notification('error', error.message || 'Failed to renew installer access.');
	}
}

// 🔥 After any grant mutation, reload the roster so revoke/renew/create reflect immediately.
export function* reloadGrantsAfterMutation() {
	yield put(partnerActions.loadGrants());
}

export default function* partnerSaga() {
	yield takeLatest(partnerActions.loadPartnerContext, getPartnerContext);
	yield takeLatest(partnerActions.loadPartnerIdentity, getPartnerIdentity);
	yield takeLatest(partnerActions.loadGrants, getGrants);
	yield takeLatest(partnerActions.createGrant, createGrant);
	yield takeLatest(partnerActions.revokeGrant, revokeGrant);
	yield takeLatest(partnerActions.renewGrant, renewGrant);
	yield takeLatest(partnerActions.createGrantSuccess, reloadGrantsAfterMutation);
	yield takeLatest(partnerActions.revokeGrantSuccess, reloadGrantsAfterMutation);
	yield takeLatest(partnerActions.renewGrantSuccess, reloadGrantsAfterMutation);
}
